/**
 * Progress for an UNGUIDED lesson run.
 *
 * The guided player needs no store at all — its cursor lives in the URL
 * (`?tour=&step=`), which is why a guided lesson survives a reload. An unguided
 * run cannot do that: what it must prove is that the learner *caused* something,
 * and that evidence lives in `useOrderingStore`, which is itself in-memory and
 * deliberately not persisted (`src/screens/ordering/store.ts:1-2`). Encoding
 * ticked tasks in the URL would therefore claim credit for work whose evidence a
 * reload has already destroyed. This store matches the cart's lifetime instead:
 * a reload starts the run clean, the same way it starts the cart clean.
 *
 * Two jobs:
 *
 * 1. LATCHING. Three real transitions delete their own evidence — the cart is
 *    cleared on submit, and `resetQpo()` runs both on print and unconditionally
 *    when the Articlepedia vendor screen mounts. A task that ticked once must
 *    stay ticked, so completion is recorded here rather than recomputed from
 *    scratch on every render.
 * 2. RECORDING what the store cannot. Routes the learner visited, and the
 *    handful of milestones emitted immediately before a destructive call.
 */
import { create } from 'zustand'

/** Events emitted right before the app destroys the evidence for them. */
export type Milestone =
  | 'order-submitted'
  | 'cart-searched'
  | 'cart-sku-deleted'
  | 'cart-sku-amended'
  | 'qpo-supplier-manual'
  | 'qpo-sku-removed'
  | 'qpo-slip-printed'
  | 'qpo-vendor-acknowledged'
  | 'history-searched'
  | 'history-goods-issued-viewed'
  | 'history-rtv-detail-viewed'
  | 'history-rsto-detail-viewed'
  | 'order-decided'
  | 'orders-date-changed'
  | 'to-receive-date-changed'
  | 'to-receive-searched'
  | 'to-receive-filtered'
  | 'receiving-driver-details-entered'
  | 'receiving-item-qty-entered'
  | 'receiving-printed'
  | 'receiving-temperatures-set'
  | 'receiving-temp-retaken'
  | 'receiving-temp-feedback-confirmed'
  | 'receiving-temp-items-verified'
  | 'receiving-shortfall-confirmed'
  | 'receiving-supplier-acknowledged'
  | 'receiving-do-held'
  | 'receiving-pallets-confirmed'
  | 'receiving-discrepancy-confirmed'
  | 'returns-vendor-searched-selected'
  | 'returns-sku-searched'
  | 'returns-item-added'
  | 'returns-scan-search-item-added'
  | 'returns-rtv-submitted'
  | 'returns-to-return-filtered'
  | 'returns-excess-stock-submitted'
  | 'returns-gi-disposal-submitted'
  | 'returns-issue-goods-submitted'
  | 'stocktake-progress-viewed'
  | 'stocktake-location-entered'
  | 'stocktake-sku-scanned'
  | 'stocktake-location-already-counted-handled'
  | 'stocktake-not-in-cycle-dismissed'
  | 'stocktake-search-used'
  | 'stocktake-max-locations-saved'
  | 'stocktake-started'
  | 'stocktake-not-scanned-viewed'
  | 'stocktake-filter-applied'
  | 'stocktake-exceptions-viewed'
  | 'stocktake-location-viewed'
  | 'stocktake-approved'
  | 'tasks-filtered'
  | 'task-viewed'
  | 'task-created-individual'
  | 'task-created-shared'
  | 'task-edited'
  | 'task-closed'
  | 'task-completed'
  | 'expiry-missing-tab-viewed'
  | 'expiry-ean-selected'
  | 'expiry-date-added'
  | 'expiry-withdrawn-confirmed'
  | 'adj-tab-viewed'
  | 'adj-detail-viewed'
  | 'adj-search-cleared'
  | 'adj-filter-applied'
  | 'adj-filters-cleared'
  | 'adj-type-selected'
  | 'adj-form-filled'
  | 'adj-add-more-items'
  | 'adj-type-selected-stocks-for-own-use'
  | 'adj-type-selected-free-goods'
  | 'adj-type-selected-break-bulk'
  | 'adj-type-selected-soh-correction'
  | 'adj-form-committed'
  | 'adj-child-sku-scanned'
  | 'adj-submitted'

interface SimProgressState {
  /** Sub-module id of the run in progress, or null when no sim is active. */
  simId: string | null
  visitedRoutes: string[]
  milestones: Milestone[]
  /** Task ids already earned. Monotonic for the lifetime of the run. */
  completed: string[]
  /** Begin a run. Clears everything from any previous run. */
  start: (simId: string) => void
  /** Record a route the learner reached. Idempotent. */
  visit: (path: string) => void
  /** Record a milestone. Idempotent. */
  emit: (milestone: Milestone) => void
  /** Latch a task as earned. Idempotent. */
  complete: (taskId: string) => void
  /** End the run and clear progress. */
  stop: () => void
}

/** The data half of the store, without the actions. */
type SimProgressData = Pick<
  SimProgressState,
  'simId' | 'visitedRoutes' | 'milestones' | 'completed'
>

/** A factory, not a shared constant — each run gets its own arrays. */
function emptyProgress(): SimProgressData {
  return { simId: null, visitedRoutes: [], milestones: [], completed: [] }
}

export const useSimProgress = create<SimProgressState>((set) => ({
  ...emptyProgress(),

  // Progress only. Resetting the ordering/receiving stores is LessonStart's job
  // and must happen BEFORE the redirect — TourLayout renders <Outlet /> ahead of
  // <TaskOverlay />, so a reset from here lands after the start screen has
  // already seeded itself from its URL params and silently wipes it.
  start: (simId) => {
    set({ ...emptyProgress(), simId })
  },

  visit: (path) =>
    set((s) =>
      s.visitedRoutes.includes(path) ? {} : { visitedRoutes: [...s.visitedRoutes, path] },
    ),

  emit: (milestone) =>
    set((s) =>
      s.milestones.includes(milestone) ? {} : { milestones: [...s.milestones, milestone] },
    ),

  complete: (taskId) =>
    set((s) => (s.completed.includes(taskId) ? {} : { completed: [...s.completed, taskId] })),

  stop: () => set(emptyProgress()),
}))

/**
 * Emit a milestone without subscribing to the store.
 *
 * Call sites are inside event handlers on real screens, one line before the
 * destructive call. Reading through `getState()` keeps those screens free of a
 * training-only subscription, so nothing about them re-renders in normal use.
 */
export function emitMilestone(milestone: Milestone): void {
  useSimProgress.getState().emit(milestone)
}
